import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { Box, Grid, TextField, Tooltip, Typography } from "@mui/material";
import { Controller, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import WorkLocationAutocomplete from "../../../../common/components/functional/autocomplete/workLocationAutocomplete/WorkLocationAutocomplete";
import useWorkLocationAutocomplete from "../../../../common/components/functional/autocomplete/workLocationAutocomplete/useWorkLocationAutocomplete";

type Props = {
  disabled?: boolean;
  showUnknownWorkLocationIdInfo?: boolean;
  unknownWorkLocationName?: string;
};

const WorkLocationControl = ({
  disabled,
  showUnknownWorkLocationIdInfo = false,
  unknownWorkLocationName,
}: Props) => {
  const { t } = useTranslation();

  const {
    control,
    watch,
    formState: { errors },
  } = useFormContext();
  const isEezyWork = watch("isEezyWork");
  const hasWorkLocationNameError = !!errors.workLocationName;
  const hasWorkLocationError = !!errors.workLocation;

  const { workLocationOptions, onInputChange, isLoading } =
    useWorkLocationAutocomplete();

  return (
    <>
      <Grid
        item
        container
        xs={3}
        justifyContent="flex-end"
        alignItems="center"
      >
        <Typography textAlign="end" variant="secondary">
          {!isEezyWork
            ? t("workHistory:workLocation")
            : t("common:workLocation")}
        </Typography>
      </Grid>
      <Grid item container xs={9} alignItems="center">
        {!isEezyWork ? (
          <Controller
            name="workLocationName"
            control={control}
            rules={{
              required: {
                value: !isEezyWork,
                message: t("common:requiredField"),
              },
            }}
            render={({ field: { name, value, onChange } }) => (
              <TextField
                autoComplete="off"
                fullWidth
                required
                name={name}
                size="small"
                value={value}
                onChange={onChange}
                error={hasWorkLocationNameError}
                disabled={disabled}
              />
            )}
          />
        ) : showUnknownWorkLocationIdInfo ? (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography>{unknownWorkLocationName || ""}</Typography>
            <Tooltip
              title={t("workHistory:unknownWorkLocationIdTooltip")}
              placement="right"
              arrow
            >
              <InfoOutlinedIcon
                fontSize="small"
                sx={{ color: "warning.main", cursor: "pointer" }}
              />
            </Tooltip>
          </Box>
        ) : (
          <Controller
            name="workLocation"
            control={control}
            rules={{
              required: {
                value: isEezyWork,
                message: t("common:requiredField"),
              },
            }}
            render={({ field }) => (
              <WorkLocationAutocomplete
                name="workLocation"
                value={field.value || null}
                options={workLocationOptions}
                onChange={(newValue) =>
                  field.onChange(newValue ? newValue : null)
                }
                onInputChange={(value, reason) => onInputChange(value, reason)}
                isLoading={isLoading}
                disabled={disabled}
                error={hasWorkLocationError}
                required
              />
            )}
          />
        )}
      </Grid>
    </>
  );
};

export default WorkLocationControl;
